// 多鏡頭攝影機（plan SPEC #multi-lens；第一台：喬安 W6-U）的純邏輯，照 component/kinio-app-core KinioLens.kt / KinioLens.swift：
//   - 鏡頭宣告 `kinio_lens`（shadow reported）解析與驗證，鏡頭名稱 / 能力的文案鍵（App 自己翻譯）
//   - `ls-ctrl` 的 lens 部分（hello caps.lens、select {lens}、state lens / lens_active / lens_blocked / 伴隨）
//   - 單畫面切換鏡頭的狀態機（已顯示 → 切換中 → 已顯示 / 失敗；冷 6 秒、熱 4 秒逾時；舊回應以 id 丟）
//   - 伴隨連線（雙畫面第二條，只要影像）的兩道核對：hub 回送的 lens / primary_session_id、主連線 state.companion
// 規則與案例：testdata/lens/kinio-lens.json、kinio-lens-switch.json（照抄 kinio-app-core docs/testdata，三平台跑同一份）。
// 單鏡頭機種（沒有 kinio_lens、或宣告不合規）一律 parseLensDecl = null：畫面與行為完全不變。

export const LENS_POSITIONS = ['upper', 'lower', 'wide', 'tele', 'left', 'right', 'other'] as const;
export const LENS_PTZ = ['pan_tilt', 'pan', 'tilt', 'none'] as const;
export const LENS_STREAMS = ['main', 'sub'] as const;
export type LensPosition = (typeof LENS_POSITIONS)[number];
export type LensPtz = (typeof LENS_PTZ)[number];

/** 換鏡頭 select 的 id 起點：跟對講共用一條 ls-ctrl（對講從 1 起），不撞號（同 app-core KinioLive.LENS_ID_BASE）。 */
export const LENS_ID_BASE = 1001;

/** 一顆鏡頭的宣告（`kinio_lens.lenses[i]`）。 */
export interface LensInfo {
  id: number;
  position: LensPosition;
  ptz: LensPtz;
  streams: string[];
  /** `capture_snapshot` 認得這顆的 `lens`。 */
  snapshot: boolean;
  /** SD 有錄這顆、`pb.timeline` 認得 `lens`。 */
  sd: boolean;
  /** 文案鍵：lens_upper / lens_lower / …；other = lens_n（顯示「鏡頭 n」，n = id + 1）。 */
  nameKey: string;
  /** 能力文案鍵：lens_ptz_pan_tilt / lens_ptz_pan / lens_ptz_tilt / lens_ptz_none。 */
  abilityKey: string;
  /** 左右能不能轉（方向盤 ◀▶）。 */
  canPan: boolean;
  /** 上下能不能轉（方向盤 ▲▼；`pan` 鏡頭按 ▲▼ 設備照轉 `pan_tilt` 那顆）。 */
  canTilt: boolean;
}

/** 這台的多鏡頭宣告（`kinio_lens` cluster）。 */
export interface LensDecl {
  lenses: LensInfo[];
  /** 一個觀看者同時可看幾顆（主連線 + 伴隨）；沒宣告 = 1。< 2 → 沒有「雙畫面」。 */
  maxLiveViews: number;
  /** 共用取像時兩顆的幀率；不共用取像 = null。 */
  sharedFps: number | null;
  /** 副鏡頭也錄影（owner 可寫）；沒宣告 = null。 */
  recordSecondary: boolean | null;
  /** off / recording / paused_low_memory / paused_upload / paused_privacy / not_effective；沒報 = null。 */
  secondaryState: string | null;
  /** 能不能雙畫面（這台）：宣告 ≥ 2 顆且 max_live_views ≥ 2。 */
  dualCapable: boolean;
  /** 能轉雲台的那顆（預置點縮圖固定取它）；沒有 = null。 */
  panTiltLens: number | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const intOr = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : d);

function lensInfo(id: number, position: LensPosition, ptz: LensPtz, streams: string[], snapshot: boolean, sd: boolean): LensInfo {
  return {
    id,
    position,
    ptz,
    streams,
    snapshot,
    sd,
    nameKey: position === 'other' ? 'lens_n' : `lens_${position}`,
    abilityKey: `lens_ptz_${ptz}`,
    canPan: ptz === 'pan_tilt' || ptz === 'pan',
    canTilt: ptz === 'pan_tilt' || ptz === 'tilt',
  };
}

/**
 * shadow reported 的 `kinio_lens`（cluster 物件）→ 宣告。不合規（id 不連續、長度 < 2 或 > 8、enum 外的值）→ null，
 * 平台與 App 都當單鏡頭（SPEC #multi-lens-cluster）。
 */
export function parseLensDecl(cluster: unknown): LensDecl | null {
  if (!isObj(cluster)) return null;
  const arr = cluster.lenses;
  if (!Array.isArray(arr) || arr.length < 2 || arr.length > 8) return null;
  const lenses: LensInfo[] = [];
  for (let i = 0; i < arr.length; i++) {
    const o = arr[i];
    if (!isObj(o) || intOr(o.id, -1) !== i) return null;
    const pos = typeof o.position === 'string' ? o.position : 'other';
    const ptz = typeof o.ptz === 'string' ? o.ptz : 'none';
    if (!(LENS_POSITIONS as readonly string[]).includes(pos) || !(LENS_PTZ as readonly string[]).includes(ptz)) return null;
    const streams = Array.isArray(o.streams) && o.streams.length > 0 ? o.streams.map(String) : ['main'];
    if (streams.some((s) => !(LENS_STREAMS as readonly string[]).includes(s))) return null;
    lenses.push(lensInfo(i, pos as LensPosition, ptz as LensPtz, streams, o.snapshot === true, o.sd === true));
  }
  const maxLiveViews = Math.min(Math.max(intOr(cluster.max_live_views, 1), 1), 8);
  const sf = intOr(cluster.shared_fps, 0);
  const sharedFps = sf >= 1 && sf <= 60 ? sf : null;
  const recordSecondary = typeof cluster.record_secondary === 'boolean' ? cluster.record_secondary : null;
  const secondaryState = typeof cluster.secondary_state === 'string' && cluster.secondary_state ? cluster.secondary_state : null;
  return {
    lenses,
    maxLiveViews,
    sharedFps,
    recordSecondary,
    secondaryState,
    dualCapable: lenses.length >= 2 && maxLiveViews >= 2,
    panTiltLens: lenses.find((l) => l.ptz === 'pan_tilt')?.id ?? null,
  };
}

/** 畫質描述鍵：該鏡頭最高解析度 ≥ 1080p = lens_quality_high（高畫質），否則 lens_quality_standard（標準畫質）。 */
export function lensQualityKey(maxHeight: number | null | undefined): 'lens_quality_high' | 'lens_quality_standard' {
  return (maxHeight ?? 0) >= 1080 ? 'lens_quality_high' : 'lens_quality_standard';
}

/** 事件 / 錄影的 `lens`：沒帶或不是非負整數 = 0（主鏡頭）。 */
export function lensOf(v: { lens?: unknown } | null | undefined): number {
  const l = v?.lens;
  return typeof l === 'number' && Number.isInteger(l) && l >= 0 ? l : 0;
}

// ---- ls-ctrl state 的鏡頭部分 ----

/** 伴隨連線（雙畫面第二條）的狀態，主連線 `state.companion`。 */
export interface CompanionState {
  /** none / active / paused / ended。 */
  state: string;
  reason: string | null;
  sessionId: string | null;
  lens: number | null;
}

/** `ls-ctrl` state 物件的鏡頭部分（多鏡頭設備才有；舊韌體 = null）。 */
export interface LensState {
  lens: number | null;
  /** 實際送出的鏡頭；回放 / 沒送 = null。 */
  lensActive: number | null;
  /** 被宿主停用的鏡頭 → 原因（例 privacy_mask）。 */
  blocked: Record<number, string>;
  shared: boolean | null;
  companionAdmitOk: boolean | null;
  companionAdmitReason: string | null;
  companion: CompanionState | null;
}

const optInt = (o: Record<string, unknown>, k: string): number | null => (typeof o[k] === 'number' ? Math.trunc(o[k] as number) : null);
const optStr = (o: Record<string, unknown>, k: string): string | null => (typeof o[k] === 'string' && o[k] ? (o[k] as string) : null);

/** `state` 物件 → 鏡頭部分；沒有 `lens` 也沒有伴隨欄位 = null（單鏡頭 / 舊韌體）。 */
export function parseLensState(state: unknown): LensState | null {
  if (!isObj(state)) return null;
  if (!('lens' in state || 'lens_active' in state || 'companion_admit' in state || 'companion' in state)) return null;
  const blocked: Record<number, string> = {};
  if (Array.isArray(state.lens_blocked)) {
    for (const b of state.lens_blocked) if (isObj(b) && typeof b.id === 'number') blocked[b.id] = typeof b.reason === 'string' ? b.reason : '';
  }
  const adm = isObj(state.companion_admit) ? state.companion_admit : null;
  const c = isObj(state.companion) ? state.companion : null;
  return {
    lens: optInt(state, 'lens'),
    lensActive: optInt(state, 'lens_active'),
    blocked,
    shared: typeof state.shared === 'boolean' ? state.shared : null,
    companionAdmitOk: adm && typeof adm.ok === 'boolean' ? adm.ok : null,
    companionAdmitReason: adm ? optStr(adm, 'reason') : null,
    companion: c ? { state: optStr(c, 'state') ?? 'none', reason: optStr(c, 'reason'), sessionId: optStr(c, 'session_id'), lens: optInt(c, 'lens') } : null,
  };
}

/** `{"id":…,"op":"select","lens":…}`（鍵序固定；帶 video 時一起套用，SPEC #multi-lens-lsctrl）。 */
export function lsCtrlSelectLens(id: number, lens: number, video?: string | null): string {
  return video == null ? `{"id":${id},"op":"select","lens":${lens}}` : `{"id":${id},"op":"select","video":"${video}","lens":${lens}}`;
}

// ---- 單畫面切換鏡頭 ----

export type LensPhase =
  | { kind: 'showing'; lens: number }
  /** 切換到 to（from = 切換前在顯示的；hot = 切換開始時設備已經在送 to）。 */
  | { kind: 'switching'; to: number; from: number; hot: boolean; startedAt: number }
  /** 切換到 lens 失敗：err = TIMEOUT / UNSUPPORTED / …；reason = 設備的原因（privacy_mask）；showing = 畫面還在播的那顆。 */
  | { kind: 'failed'; lens: number; err: string; reason: string | null; showing: number };

/**
 * 單畫面切換鏡頭（SPEC #multi-lens-live「切換鏡頭（單畫面）」）。時間（ms）由呼叫端帶入、不碰計時器。
 * 成功 = `state.lens_active == j` **而且**之後第一個影格到（onFrame）；冷 6 秒、熱 4 秒逾時；舊回應以 id 丟；
 * 設備自己切回（隱私遮罩）畫面跟著設備。
 */
export class LensSwitch {
  static readonly COLD_TIMEOUT_MS = 6000;
  static readonly HOT_TIMEOUT_MS = 4000;

  phase: LensPhase;
  /** 設備實際在送的鏡頭（state.lens_active）；不知道 = null。 */
  lensActive: number | null = null;
  private nextId: number;
  private pendingId: number | null = null;
  private activeSeenForTarget = false;

  constructor(initialLens = 0, firstId = 1) {
    this.phase = { kind: 'showing', lens: initialLens };
    this.nextId = firstId;
  }

  /** 畫面上現在播的那顆（切換中 = 切換前那顆；失敗 = 還在播的那顆）。 */
  get showing(): number {
    const p = this.phase;
    return p.kind === 'showing' ? p.lens : p.kind === 'switching' ? p.from : p.showing;
  }

  /** 使用者點了鏡頭 lens：回要送的 ls-ctrl frame；已經在顯示它 = null。 */
  select(lens: number, now: number, video?: string | null): string | null {
    const p = this.phase;
    if (p.kind === 'showing' && p.lens === lens) return null;
    const id = this.nextId++;
    this.pendingId = id;
    this.activeSeenForTarget = false;
    this.phase = { kind: 'switching', to: lens, from: this.showing, hot: this.lensActive === lens, startedAt: now };
    return lsCtrlSelectLens(id, lens, video);
  }

  /** ls-ctrl response。不是這次的 id = 丟掉。 */
  onResponse(id: number | null, ok: boolean, err: string | null, reason: string | null, state: LensState | null): LensPhase {
    if (state?.lensActive != null) this.lensActive = state.lensActive;
    const p = this.phase;
    if (p.kind === 'failed' && state?.lensActive === p.lens) this.activeSeenForTarget = true; // 逾時後晚到的成功
    if (id === null || id !== this.pendingId || p.kind !== 'switching') return this.phase;
    this.pendingId = null;
    if (!ok) {
      this.phase = { kind: 'failed', lens: p.to, err: err ?? 'ERROR', reason, showing: p.from };
      return this.phase;
    }
    if (state?.lensActive === p.to) this.activeSeenForTarget = true;
    return this.phase;
  }

  /** ls-ctrl state event：記 lens_active；切換中 / 失敗中看到目標就等首幀。 */
  onState(state: LensState | null): LensPhase {
    const la = state?.lensActive ?? null;
    this.lensActive = la;
    const p = this.phase;
    if (p.kind === 'switching') {
      if (la === p.to) this.activeSeenForTarget = true;
    } else if (p.kind === 'failed') {
      if (la === p.lens) this.activeSeenForTarget = true;
    } else if (la !== null && la !== p.lens) {
      this.phase = { kind: 'showing', lens: la }; // 設備自己切了（隱私遮罩把看副鏡頭的切回 lens 0）
    }
    return this.phase;
  }

  /** 收到一個影格（呼叫端每格叫也沒關係）。 */
  onFrame(): LensPhase {
    const p = this.phase;
    if (p.kind === 'switching' && this.activeSeenForTarget) this.phase = { kind: 'showing', lens: p.to };
    else if (p.kind === 'failed' && this.activeSeenForTarget) this.phase = { kind: 'showing', lens: p.lens };
    return this.phase;
  }

  /** 計時：切換中超過冷 6 秒 / 熱 4 秒 → 失敗（TIMEOUT）。 */
  tick(now: number): LensPhase {
    const p = this.phase;
    if (p.kind === 'switching' && now - p.startedAt >= (p.hot ? LensSwitch.HOT_TIMEOUT_MS : LensSwitch.COLD_TIMEOUT_MS)) {
      this.pendingId = null;
      this.phase = { kind: 'failed', lens: p.to, err: 'TIMEOUT', reason: null, showing: p.from };
    }
    return this.phase;
  }

  /** 主連線重連 / 換了連線：回到 lens 已顯示。 */
  reset(lens: number): void {
    this.pendingId = null;
    this.lensActive = null;
    this.activeSeenForTarget = false;
    this.phase = { kind: 'showing', lens };
  }
}

// ---- 伴隨連線核對 ----

/** 被拒沒帶 `retry_after_ms` 時等多久再試（ms）。 */
export const COMPANION_DEFAULT_RETRY_MS = 5000;

/** ① hub 回送核對：都要有而且一樣（舊 hub 會連 primary_session_id 一起丟）。 */
export function companionEchoOk(wantLens: number, wantPrimary: string, gotLens: number | null, gotPrimary: string | null): boolean {
  return gotLens === wantLens && gotPrimary === wantPrimary;
}

/** ② 首幀核對：主連線最後一個 state 的 companion 是這條、這顆。state 還沒說或不是 active / paused = true（不擋）。 */
export function companionStateOk(mainState: LensState | null, companionSessionId: string, lens: number): boolean {
  const c = mainState?.companion;
  if (!c || (c.state !== 'active' && c.state !== 'paused')) return true;
  return (c.sessionId === null || c.sessionId === companionSessionId) && (c.lens === null || c.lens === lens);
}

/** 伴隨被拒的錯誤要不要自動重試一次（`low_memory` 不重試；隱私遮罩 / 核對失敗不重試）。 */
export function companionRetryOnce(code: string, reason: string | null): boolean {
  if (code === 'VIEWER_LIMIT' || code === 'MAX_SESSIONS_REACHED') return true;
  if (code === 'DEVICE_BUSY') return reason !== 'low_memory';
  return false;
}

// ---- ls-ctrl 訊息的鏡頭部分（對講部分在 lsctrl.ts parseLsCtrl） ----

export type LsCtrlLensMessage =
  /** hello `caps.lens`（靜態宣告；null = 沒有 = 只有主鏡頭 / 舊韌體）。 */
  | { kind: 'hello'; lens: number[] | null }
  | { kind: 'response'; id: number | null; ok: boolean; err: string | null; reason: string | null; state: LensState | null }
  | { kind: 'state'; reason: string | null; state: LensState | null };

/** 解一個 ls-ctrl text frame 的鏡頭部分。不是 hello / state / response → null。 */
export function parseLsCtrlLens(text: string): LsCtrlLensMessage | null {
  let o: unknown;
  try {
    o = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isObj(o)) return null;
  const reason = optStr(o, 'reason');
  if (typeof o.ev === 'string') {
    if (o.ev === 'hello') {
      const caps = isObj(o.caps) ? o.caps : {};
      const lens = Array.isArray(caps.lens) ? caps.lens.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0) : null;
      return { kind: 'hello', lens: lens && lens.length > 0 ? lens : null };
    }
    if (o.ev === 'state') return { kind: 'state', reason, state: parseLensState(o.state) };
    return null;
  }
  if (!('op' in o) && 'ok' in o) {
    return { kind: 'response', id: typeof o.id === 'number' ? o.id : null, ok: o.ok === true, err: optStr(o, 'err'), reason, state: parseLensState(o.state) };
  }
  return null;
}
